import { Component } from '@theme/component';
import { BarcodeScannerUnresolvedEvent } from '@theme/events';

/**
 * Public Storefront API access token (not the Admin API — safe to ship in theme JS, same
 * token already committed to this repo and used by assets/collection-discounts.js).
 */
const STOREFRONT_ACCESS_TOKEN = '8cf80f132e120b3074efc13de9e668e0';
const STOREFRONT_API_VERSION = '2025-10';

/**
 * Common 1D formats found on retail product packaging. Narrowing to these (rather than every
 * format both BarcodeDetector and ZXing can decode, which also includes 2D formats like QR)
 * makes detection faster and avoids false positives from unrelated codes in frame.
 */
const BARCODE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'];

/**
 * ZXing's format enum uses its own names/casing for the same formats.
 * @see assets/zxing-library.min.js (vendored @zxing/library UMD build)
 */
const ZXING_BARCODE_FORMAT_NAMES = ['EAN_13', 'EAN_8', 'UPC_A', 'UPC_E', 'CODE_128', 'CODE_39', 'ITF'];

const BARCODE_LOOKUP_QUERY = `#graphql
  query BarcodeLookup($query: String!) {
    search(query: $query, types: PRODUCT, first: 1) {
      edges {
        node {
          ... on Product {
            handle
          }
        }
      }
    }
  }
`;

/**
 * @param {string} query
 * @param {Record<string, unknown>} variables
 * @param {AbortSignal} signal
 */
async function storefrontFetch(query, variables, signal) {
  const domain = window.routes?.permanentDomain;
  const response = await fetch(`https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
    signal,
  });

  if (!response.ok) throw new Error(`Storefront API request failed: ${response.status}`);

  const { data, errors } = await response.json();
  if (errors?.length) throw new Error(errors[0]?.message ?? 'Storefront API returned errors');

  return data;
}

/**
 * Scans a product barcode with the device camera and redirects to the matching product. Used as
 * the header search dialog's barcode view (see snippets/barcode-scanner-view.liquid,
 * snippets/search-modal.liquid) — assets/search-modal-body.js owns recovery whenever a scan
 * doesn't resolve, via the cancelable BarcodeScannerUnresolvedEvent dispatched below.
 *
 * Uses the native BarcodeDetector API where available, falling back to a vendored ZXing build
 * (assets/zxing-library.min.js, loaded on demand) on browsers that lack it — notably Safari/iOS,
 * which as of writing ships BarcodeDetector only behind a manual flag.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video
 * @property {HTMLElement} cameraWrap
 * @property {HTMLElement} status
 * @property {HTMLButtonElement} startButton
 * @extends {Component<Refs>}
 */
class BarcodeScannerComponent extends Component {
  requiredRefs = ['video', 'cameraWrap', 'status', 'startButton'];

  /** @type {MediaStream | null} */
  #stream = null;

  /** @type {number | null} */
  #detectRafId = null;

  /** @type {any} */
  #zxingReader = null;

  /** @type {Promise<void> | null} */
  #zxingLoadPromise = null;

  #lookupController = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopScanning();
    this.#lookupController.abort();
  }

  async startScanning() {
    this.refs.startButton.disabled = true;

    if (!navigator.mediaDevices?.getUserMedia) {
      this.#handleCameraUnavailable();
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
    } catch {
      this.#handleCameraUnavailable();
      return;
    }

    this.#stream = stream;
    this.refs.video.srcObject = stream;
    await this.refs.video.play().catch(() => {});

    this.refs.startButton.hidden = true;
    this.refs.cameraWrap.hidden = false;
    this.#setStatus(this.dataset.msgScanning ?? '');

    if ('BarcodeDetector' in window) {
      const started = await this.#startNativeDetection();
      if (started) return;
    }

    await this.#startZxingDetection();
  }

  /**
   * @returns {Promise<boolean>} Whether native detection actually started.
   */
  async #startNativeDetection() {
    try {
      const supportedFormats = await window.BarcodeDetector.getSupportedFormats();
      const formats = BARCODE_FORMATS.filter((format) => supportedFormats.includes(format));
      if (formats.length === 0) return false;

      const detector = new window.BarcodeDetector({ formats });
      this.#runNativeDetectLoop(detector);
      return true;
    } catch {
      return false;
    }
  }

  /** @param {any} detector */
  #runNativeDetectLoop(detector) {
    const tick = async () => {
      if (!this.#stream) return;

      try {
        const results = await detector.detect(this.refs.video);
        const value = results[0]?.rawValue;
        if (value) {
          this.#handleDecoded(value);
          return;
        }
      } catch {
        // Transient per-frame decode errors (e.g. video not ready yet) are expected — keep polling.
      }

      this.#detectRafId = requestAnimationFrame(tick);
    };

    this.#detectRafId = requestAnimationFrame(tick);
  }

  async #startZxingDetection() {
    try {
      await this.#loadZxing();
    } catch {
      this.#handleScanningUnavailable();
      return;
    }

    if (!this.#stream) return; // Stopped while the library was loading.

    const ZXing = /** @type {any} */ (window).ZXing;
    const hints = new Map();
    hints.set(
      ZXing.DecodeHintType.POSSIBLE_FORMATS,
      ZXING_BARCODE_FORMAT_NAMES.map((name) => ZXing.BarcodeFormat[name])
    );

    const reader = new ZXing.BrowserMultiFormatReader(hints);
    this.#zxingReader = reader;

    reader.decodeFromStream(this.#stream, this.refs.video, (result, error) => {
      if (result) {
        this.#handleDecoded(result.getText());
      } else if (error && !(error instanceof ZXing.NotFoundException)) {
        // A real decode failure (not just "no barcode in this frame") — fall back to search
        // rather than leaving the shopper staring at a camera that will never succeed.
        this.#handleScanningUnavailable();
      }
    });
  }

  /** @returns {Promise<void>} */
  #loadZxing() {
    if (window.ZXing) return Promise.resolve();
    if (this.#zxingLoadPromise) return this.#zxingLoadPromise;

    const src = this.dataset.zxingSrc;
    if (!src) return Promise.reject(new Error('No ZXing fallback script configured'));

    this.#zxingLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load ZXing fallback script'));
      document.head.append(script);
    });

    return this.#zxingLoadPromise;
  }

  /** @param {string} code */
  async #handleDecoded(code) {
    this.#stopScanning();
    this.#setStatus(this.dataset.msgLookingUp ?? '');

    const trimmedCode = code.trim();

    try {
      // `variants.barcode:` (dotted) is the field-scoped search filter that actually works
      // against the Storefront API's `search` query — the underscore form some docs show
      // (`variants_barcode:`) silently falls back to unscoped full-text matching instead of
      // filtering, which would send shoppers to an unrelated product on almost every scan.
      const data = await storefrontFetch(
        BARCODE_LOOKUP_QUERY,
        { query: `variants.barcode:${trimmedCode}` },
        this.#lookupController.signal
      );

      const handle = data?.search?.edges?.[0]?.node?.handle;
      if (handle) {
        window.location.href = this.#productUrl(handle);
        return;
      }

      this.#setStatus(this.dataset.msgNotFound ?? '');
      this.#notifyUnresolved('not_found');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      this.#setStatus(this.dataset.msgLookupError ?? '');
      this.#notifyUnresolved('lookup_error');
    }
  }

  /** @param {string} handle */
  #productUrl(handle) {
    const root = this.dataset.rootUrl ?? '/';
    return `${root.replace(/\/$/, '')}/products/${handle}`;
  }

  #handleCameraUnavailable() {
    this.refs.startButton.hidden = true;
    this.#setStatus(this.dataset.msgCameraUnavailable ?? '');
    this.#notifyUnresolved('camera_unavailable');
  }

  #handleScanningUnavailable() {
    this.#stopScanning();
    this.#setStatus(this.dataset.msgScanningUnavailable ?? '');
    this.#notifyUnresolved('scanning_unavailable');
  }

  /**
   * Notifies assets/search-modal-body.js that this scan attempt didn't end in a redirect, so it
   * can revert to the normal search view with its own notice.
   * @param {'camera_unavailable' | 'scanning_unavailable' | 'not_found' | 'lookup_error'} reason
   */
  #notifyUnresolved(reason) {
    this.dispatchEvent(new BarcodeScannerUnresolvedEvent(reason));
  }

  #stopScanning() {
    if (this.#detectRafId !== null) {
      cancelAnimationFrame(this.#detectRafId);
      this.#detectRafId = null;
    }

    if (this.#zxingReader) {
      try {
        this.#zxingReader.reset();
      } catch {
        // Reader may already be stopped/disposed — nothing else to clean up here.
      }
      this.#zxingReader = null;
    }

    if (this.#stream) {
      for (const track of this.#stream.getTracks()) track.stop();
      this.#stream = null;
    }

    this.refs.video.srcObject = null;
  }

  /** @param {string} text */
  #setStatus(text) {
    this.refs.status.textContent = text;
  }

  /**
   * Stops the camera and puts the component back in its pre-scan state, so it can be reused
   * across multiple open/close cycles instead of always starting from a fresh page load — see
   * assets/search-modal-body.js, which reuses one instance for the lifetime of the search dialog.
   */
  reset() {
    this.#stopScanning();

    // Aborts any lookup fetch still in flight from a previous scan attempt — without this, a
    // reused instance (see assets/search-modal-body.js) could redirect a shopper to a stale
    // barcode's product after they've already moved on to scanning a different one.
    this.#lookupController.abort();
    this.#lookupController = new AbortController();

    this.refs.startButton.disabled = false;
    this.refs.startButton.hidden = false;
    this.refs.cameraWrap.hidden = true;
    this.#setStatus(this.dataset.msgPermissionPrompt ?? '');
  }
}

if (!customElements.get('barcode-scanner-component')) {
  customElements.define('barcode-scanner-component', BarcodeScannerComponent);
}
